import type { CustomField, CustomFieldOption } from '@/types/product';

/**
 * Helpers for customer-submitted values of a product custom field.
 *
 * A `multiSelect` field lets the customer tick several priced options at once. Its
 * Inputs entry carries BOTH shapes so legacy readers (order details view, invoices,
 * emails) keep rendering something sensible:
 *   Inputs[fieldId] = { value: "A, B", values: ["A", "B"], title, fieldType: "multiSelect" }
 */

const MULTI_SELECT_TYPES = ['multiSelect', 'multiselect', 'multiChoice', 'checkboxGroup'];

export type FieldValue = string | string[];

export function isMultiSelectField(fieldType?: string): boolean {
  return !!fieldType && MULTI_SELECT_TYPES.includes(fieldType);
}

/** Normalize a stored value into the array of ticked option names. */
export function toValueArray(raw: unknown): string[] {
  if (raw === null || raw === undefined) return [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.values)) return (obj.values as unknown[]).map(String).filter(Boolean);
    return toValueArray(obj.value);
  }
  const str = String(raw);
  return str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];
}

/** Flat display string for a value that may be an array. */
export function toDisplayValue(raw: unknown): string {
  if (Array.isArray(raw)) return raw.filter(Boolean).join(', ');
  if (raw === null || raw === undefined) return '';
  return String(raw);
}

export function isValueFilled(raw: unknown): boolean {
  if (Array.isArray(raw)) return raw.length > 0;
  if (raw === null || raw === undefined) return false;
  return String(raw).trim() !== '';
}

/** Toggle one option of a multiSelect value, keeping the field's own option order. */
export function toggleValue(current: string[], value: string, options?: CustomFieldOption[]): string[] {
  const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
  if (!options?.length) return next;
  const order = options.map(o => o.name);
  return next.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

/**
 * Build the Inputs entry the backend expects — formatItems.js matches `title` and
 * `fieldType` against the product's field definition, so both must be sent as-is.
 */
export function buildInputEntry(field: CustomField | undefined, fieldId: string, value: FieldValue) {
  const entry: Record<string, unknown> = {
    value: toDisplayValue(value),
    title: field?.title ?? fieldId,
    fieldType: field?.fieldType ?? 'text',
  };
  if (isMultiSelectField(field?.fieldType)) entry.values = toValueArray(value);
  return entry;
}
